import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, X, RefreshCw, Link as LinkIcon } from 'lucide-react';

// ✅ اتصال واقعی به سرویس بک‌ند (فایل ایجاد شده در بالا)
import { sendChatQuery } from '../../services/chatService';

// تابع تولید UUID داخلی برای کاهش وابستگی‌ها
const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

const ChatWindow = ({ onClose }) => {
    // متن خوش‌آمدگویی کامل
    const initialText = `سلام. من دستیار هوشمند «فرزانه» هستم، طراحی شده‌ام تا شما را در درک عمیق‌تر دیدگاه‌ها و آثار استاد مصطفی ملکیان همراهی کنم. من با دسترسی به سخنرانی‌ها، کتاب‌ها و مقالات مرتبط، جامع‌ترین پاسخ‌ها را ارائه می‌دهم.

شما می‌توانید در مورد این موارد از من بپرسید:
* مفاهیم اصلی در فلسفه ملکیان (مانند خردورزی، اخلاق، معنویت).
* مطالب ارائه شده در درس‌گفتارها و دوره‌های سایت.
* توصیه کتاب‌ها و مقالات مرتبط با موضوع مورد نظر شما.

برای مثال:
* «در کدام سخنرانی‌ها و درس گفتارهای استاد ملکیان به آثار میشل فوکو اشاره شده است؟»
* «کدام آثار استاد در ارتباط با روانشناسی مثبت نگر است؟»
* «رابطه عقلانیت و معنویت از دیدگاه ایشان چیست؟»

لطفاً پرسش خود را مطرح کنید.`;

    const INITIAL_MESSAGE = {
        role: 'assistant',
        text: initialText,
        sources: []
    };

    const [messages, setMessages] = useState([INITIAL_MESSAGE]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    // اسکرول خودکار به پایین با هر پیام جدید
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isLoading]);

    // --- تابع شروع مکالمه جدید ---
    const handleNewChat = () => {
        // ۱. ریست کردن پیام‌ها به حالت اولیه
        setMessages([INITIAL_MESSAGE]);
        // ۲. تولید شناسه جدید برای کاربر مهمان (پاک کردن حافظه سرور برای این کاربر)
        localStorage.setItem('ai_guest_id', generateUUID());
    };

    // --- افکت تایپ (Typewriter) ---
    const typeWriterEffect = (text, sources) => {
        let index = -1;
        // اضافه کردن یک پیام خالی که قرار است پر شود
        setMessages(prev => [...prev, { role: 'assistant', text: '', sources: [] }]);

        // سرعت تایپ را کمی سریع‌تر (۵ میلی‌ثانیه) تنظیم کردیم تا کاربر منتظر نماند
        const interval = setInterval(() => {
            index++;
            if (index < text.length) {
                setMessages(prev => {
                    const newMsgs = [...prev];
                    const lastMsg = newMsgs[newMsgs.length - 1];
                    lastMsg.text = text.substring(0, index + 1);
                    return newMsgs;
                });
            } else {
                clearInterval(interval);
                // وقتی تایپ تمام شد، منابع را اضافه می‌کنیم
                if (sources?.length > 0) {
                     setMessages(prev => {
                        const newMsgs = [...prev];
                        newMsgs[newMsgs.length - 1].sources = sources;
                        return newMsgs;
                    });
                }
            }
        }, 5);
    };

    // --- ارسال پیام به بک‌ند ---
    const handleSend = async () => {
        if (!input.trim() || isLoading) return;
        const userQuery = input;
        setInput('');
        
        // ۱. نمایش پیام کاربر
        setMessages(prev => [...prev, { role: 'user', text: userQuery }]);
        setIsLoading(true);

        try {
            // ۲. ارسال درخواست واقعی به سرور
            // توکن null ارسال می‌شود چون برای کاربران مهمان طراحی شده است
            const data = await sendChatQuery(userQuery, null);
            
            setIsLoading(false);
            
            // ۳. نمایش پاسخ سرور با افکت تایپ
            typeWriterEffect(data.text, data.sources);

        } catch (error) {
            console.error("Chat Error:", error);
            setIsLoading(false);
            setMessages(prev => [...prev, { 
                role: 'assistant', 
                text: 'متأسفانه مشکلی در برقراری ارتباط با سرور پیش آمده است. لطفاً اتصال اینترنت خود را بررسی کرده و دوباره تلاش کنید.' 
            }]);
        }
    };

    // --- رندر کننده متن (پشتیبانی از بولد، لینک و لیست) ---
    const renderMessageText = (text) => {
        if (!text) return null;
        return text.split('\n').map((line, i) => {
            // نمایش لیست‌ها (خطوطی که با * شروع می‌شوند)
            if (line.trim().startsWith('* ')) {
                return (
                    <div key={i} className="flex items-start gap-2 my-1 pr-2">
                        <span className="text-blue-400 mt-1.5 shrink-0">•</span>
                        <span>{parseInlineStyles(line.replace('* ', ''))}</span>
                    </div>
                );
            }
            // خطوط خالی برای فاصله
            if (line.trim() === '') {
                return <div key={i} className="h-2"></div>;
            }
            // پاراگراف‌های معمولی
            return <p key={i} className="mb-1 leading-relaxed">{parseInlineStyles(line)}</p>;
        });
    };

    // پردازش استایل‌های درون‌خطی (لینک و بولد)
    const parseInlineStyles = (text) => {
        // تشخیص لینک‌های مارک‌داون: [عنوان](لینک)
        const parts = text.split(/(\[.*?\]\(.*?\))/g);
        return parts.map((part, index) => {
            const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
            if (linkMatch) {
                return (
                    <a 
                        key={index} 
                        href={linkMatch[2]} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-blue-600 underline hover:text-blue-800 font-medium mx-1"
                    >
                        {linkMatch[1]}
                    </a>
                );
            }
            // در اینجا می‌توان منطق Bold (**text**) را هم اضافه کرد، فعلاً متن ساده برمی‌گرداند
            return part;
        });
    };

    return (
        <div 
            dir="rtl"
            className="w-full h-full bg-white flex flex-col font-sans overflow-hidden rounded-t-2xl shadow-2xl border border-gray-200"
        >
            {/* --- هدر پنجره --- */}
            <div className="bg-gradient-to-b from-[#5697c9] to-[rgb(128,70,227)]

            text-white p-3 flex justify-between items-center shadow-md shrink-0 select-none">
                <div className="flex items-center gap-2 font-bold text-[11px]">
                    <div className="bg-white/20 p-1.5 rounded-full">
                        <Bot size={16} />
                    </div>
                    <span>دستیار هوشمند فرزانه</span>
                </div>
                <div className="flex gap-2">
                    <button 
                        onClick={handleNewChat} 
                        title="مکالمه جدید" 
                        className="hover:bg-white/20 p-1.5 rounded transition text-purple-100 hover:text-white"
                    >
                        <RefreshCw size={12} />
                    </button>
                    <button 
                        onClick={onClose} 
                        title="بستن" 
                        className="hover:bg-red-500 p-1.5 rounded transition ml-1"
                    >
                        <X size={14} />
                    </button>
                </div>
            </div>

            {/* --- بدنه پیام‌ها --- */}
            <div className="flex-1 overflow-y-auto p-3 bg-gray-50 space-y-4 text-right">
                {messages.map((msg, idx) => (
                    <div key={idx} className={`flex w-full ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        
                        {/* آواتار ربات (سمت راست پیام) */}
                        {msg.role === 'assistant' && (
                            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 ml-2 shrink-0 mt-1">
                                <Bot size={12} />
                            </div>
                        )}

                        {/* حباب پیام */}
                        <div className={`
                            max-w-[85%] p-2.5 text-xs shadow-sm overflow-hidden
                            ${msg.role === 'user' 
                                ? 'bg-purple-600 text-white rounded-2xl rounded-br-none' 
                                : 'bg-white text-gray-800 border border-gray-200 rounded-2xl rounded-bl-none'
                            }
                        `}>
                            {/* متن پیام */}
                            <div className={msg.role === 'user' ? 'text-white' : 'text-gray-800'}>
                                {msg.role === 'user' ? msg.text : renderMessageText(msg.text)}
                            </div>

                            {/* بخش منابع و ارجاعات */}
                            {msg.sources && msg.sources.length > 0 && (
                                <div className={`mt-2 pt-1 border-t flex flex-wrap gap-1 ${msg.role === 'user' ? 'border-white/20' : 'border-gray-100'}`}>
                                    {msg.sources.map((src, i) => (
                                        <a 
                                            key={i} 
                                            href={src.uri} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className={`text-[10px] px-1.5 py-0.5 rounded truncate max-w-[120px] transition flex items-center gap-1
                                                ${msg.role === 'user' 
                                                    ? 'bg-purple-700 text-blue-100 hover:bg-blue-800' 
                                                    : 'bg-purple-50 text-blue-600 hover:bg-blue-100 border border-blue-100'
                                                }
                                            `}
                                            title={src.title}
                                        >
                                            <LinkIcon size={10} /> {src.title || 'منبع'}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* آواتار کاربر (سمت چپ پیام) */}
                        {msg.role === 'user' && (
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 mr-2 shrink-0 self-end mb-1">
                                <User size={10} />
                            </div>
                        )}
                    </div>
                ))}
                
                {/* نمایشگر وضعیت نوشتن */}
                {isLoading && (
                    <div className="flex items-center gap-2 text-gray-400 text-[10px] px-2 animate-pulse">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                        <span className="mr-1">فرزانه در حال نوشتن...</span>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* --- فوتر (ورودی متن) --- */}
            <div className="p-2 bg-white border-t border-gray-200 flex items-end gap-2 shrink-0">
                <textarea 
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                        }
                    }}
                    placeholder="سوال خود را بپرسید..."
                    disabled={isLoading}
                    rows={1}
                    className="flex-1 bg-gray-100 text-gray-800 text-[11px] rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-blue-500 transition resize-none max-h-20 overflow-y-auto min-h-[40px]"
                />
                <button 
                    onClick={handleSend} 
                    disabled={!input.trim() || isLoading}
                    className={`
                        p-2.5 rounded-lg transition-all duration-200 flex items-center justify-center h-[40px] w-[40px]
                        ${!input.trim() || isLoading 
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                            : 'bg-blue-600 text-white hover:bg-blue-700 shadow hover:shadow-md'
                        }
                    `}
                >
                    <Send size={14} className={input.trim() && !isLoading ? '-ml-0.5' : ''} /> 
                </button>
            </div>
        </div>
    );
};

export default ChatWindow;